import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ChevronRight, BookOpen, Users, FileText, Sparkles } from "lucide-react";

const LIFE_STORY_STEPS = [
  { id: "name", q: "What is your name?", type: "text", placeholder: "Your first name" },
  { id: "journey", q: "Which feels closest to your journey?", type: "choice", options: ["Steady path", "Taken turns", "Figuring it out", "Complicated"] },
  { id: "shaped", q: "What shaped you most?", type: "choice", options: ["Career", "Failure", "Relationship", "Relocation", "Risk"] },
  { id: "noticed", q: "What do people notice about you?", type: "choice", options: ["Strong", "Evolving", "Decisive", "Stable", "Curious"] },
  { id: "era", q: "Which era is most cherished?", type: "choice", options: ["Childhood", "College", "Marriage", "Professional"] },
  { id: "era_detail", q: "Tell us a bit more about your {era}.", type: "text", placeholder: "Share a brief memory...", multiline: true },
];

const SHORT_STORY_STEPS = [
  { id: "name", q: "What is your name?", type: "text", placeholder: "Your first name" },
  { id: "phase", q: "Which phase of life does this incident belong to?", type: "choice", options: ["Childhood", "College", "Relationship", "Professional", "Travel"] },
  { id: "emotion", q: "What is the core emotion?", type: "choice", options: ["Funny", "Heartwarming", "Tough lesson", "Proud", "Chaos"] },
  { id: "who", q: "Who else is in this memory?", type: "choice", options: ["Just me", "Family", "Friend", "Colleague", "Stranger"] },
  { id: "summary", q: "Give a quick 2-line summary about what happened.", type: "text", placeholder: "What happened in brief...", multiline: true },
];

const OnboardingModal = ({ onComplete, onClose }) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState(null); // null | "life" | "short" | "group"
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  const steps = mode === "life" ? LIFE_STORY_STEPS : mode === "short" ? SHORT_STORY_STEPS : [];

  const currentStep = steps[step];

  const handleModeSelect = (m) => {
    if (m === "group") {
      localStorage.setItem("onboarded", "true");
      onClose();
      navigate("/occasions");
      return;
    }
    setMode(m);
    setStep(0);
    setAnswers({});
  };

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [currentStep.id]: value };
    setAnswers(newAnswers);

    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      // Done — inject into AI and close
      localStorage.setItem("onboarded", "true");
      onComplete({ mode, answers: newAnswers });
    }
  };

  // Dynamic question text
  const getQuestionText = () => {
    if (!currentStep) return "";
    let q = currentStep.q;
    if (q.includes("{era}") && answers.era) {
      q = q.replace("{era}", answers.era);
    }
    return q;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="onboarding-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2D3A2D] to-[#4A5D4A] px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="font-serif text-xl text-white">
              {!mode ? "How would you like to start?" : `Step ${step + 1} of ${steps.length}`}
            </h2>
            {mode && <div className="w-full bg-white/20 rounded-full h-1 mt-2"><div className="bg-white rounded-full h-1 transition-all" style={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div>}
          </div>
          <button onClick={() => { localStorage.setItem("onboarded", "true"); onClose(); }} className="text-white/60 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {!mode ? (
            /* Mode Selection */
            <div className="space-y-3" data-testid="onboarding-mode-select">
              {[
                { id: "life", icon: BookOpen, title: "Life Story", desc: "A comprehensive memoir of your journey", color: "text-amber-600" },
                { id: "short", icon: FileText, title: "Short Story", desc: "A single memorable incident or episode", color: "text-blue-600" },
                { id: "group", icon: Users, title: "Group Book / Events", desc: "Collect memories from friends & family", color: "text-green-600" },
              ].map(m => (
                <button key={m.id} onClick={() => handleModeSelect(m.id)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-[#D4C9B8]/40 hover:border-[#7A9B7E] hover:bg-[#F8F6F1] transition-all text-left group"
                  data-testid={`onboarding-${m.id}`}>
                  <div className={`w-10 h-10 rounded-lg bg-[#F8F6F1] flex items-center justify-center ${m.color}`}>
                    <m.icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-sans font-medium text-[#2D3A2D]">{m.title}</p>
                    <p className="font-sans text-xs text-[#7A8A7A]">{m.desc}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#D4C9B8] group-hover:text-[#7A9B7E]" />
                </button>
              ))}
            </div>
          ) : (
            /* Question Step */
            <div data-testid="onboarding-step">
              <p className="font-sans text-base text-[#2D3A2D] mb-5">{getQuestionText()}</p>

              {currentStep.type === "choice" ? (
                <div className="grid grid-cols-2 gap-2">
                  {currentStep.options.map(opt => (
                    <button key={opt} onClick={() => handleAnswer(opt)}
                      className="px-4 py-3 rounded-xl border border-[#D4C9B8]/40 font-sans text-sm text-[#4A5D4A] hover:border-[#7A9B7E] hover:bg-[#7A9B7E]/10 transition-all text-left"
                      data-testid={`choice-${opt.toLowerCase().replace(/\s/g, "-")}`}>
                      {opt}
                    </button>
                  ))}
                </div>
              ) : currentStep.multiline ? (
                <div className="space-y-3">
                  <textarea
                    placeholder={currentStep.placeholder}
                    className="w-full px-4 py-3 rounded-xl border border-[#D4C9B8]/50 bg-[#F8F6F1] font-sans text-sm text-[#2D3A2D] focus:outline-none focus:ring-2 focus:ring-[#7A9B7E]/40 resize-none"
                    rows={3}
                    data-testid="onboarding-textarea"
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && e.target.value.trim()) { e.preventDefault(); handleAnswer(e.target.value.trim()); } }}
                  />
                  <button onClick={() => {
                    const el = document.querySelector('[data-testid="onboarding-textarea"]');
                    if (el?.value.trim()) handleAnswer(el.value.trim());
                  }} className="px-6 py-2 rounded-full bg-[#4A5D4A] text-white font-sans text-sm hover:bg-[#2D3A2D] transition-all" data-testid="onboarding-next">
                    Continue <ChevronRight className="w-3 h-3 inline ml-1" />
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder={currentStep.placeholder}
                    className="w-full px-4 py-3 rounded-xl border border-[#D4C9B8]/50 bg-[#F8F6F1] font-sans text-sm text-[#2D3A2D] focus:outline-none focus:ring-2 focus:ring-[#7A9B7E]/40"
                    data-testid="onboarding-input"
                    onKeyDown={e => { if (e.key === "Enter" && e.target.value.trim()) handleAnswer(e.target.value.trim()); }}
                  />
                  <button onClick={() => {
                    const el = document.querySelector('[data-testid="onboarding-input"]');
                    if (el?.value.trim()) handleAnswer(el.value.trim());
                  }} className="px-6 py-2 rounded-full bg-[#4A5D4A] text-white font-sans text-sm hover:bg-[#2D3A2D] transition-all" data-testid="onboarding-next">
                    Continue <ChevronRight className="w-3 h-3 inline ml-1" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
